import * as moment from 'moment';
import Moment = moment.Moment;
import 'moment/locale/da';
import {environment} from '../../environments/environment';

const DG_ICON_COUNT = 13;

export class ShipmentProductLine {

    static _JSONProps: string[] = [
        'image_url',
        'id',
        'shipment_id',
        'shipment_package_id',
        'external_reference',
        'number',
        'product_number',
        'product_number_ean',
        'purchase_order',
        'product_text_1',
        'product_text_2',
        'product_text_3',
        'amount',
        'original_amount',
        'amount_picked',
        'unit',
        'status',
        'shipment_status_formatted',
        'note',
        'stock_location',
        'stock',
        'stock_reserved',
        'weight',
        'volume',
        'width',
        'height',
        'length',
        'packaging_code',
        'dg_un',
        'dg_class',
        'dg_packaging_group',
        'dg_description',
        'dg_packaging',
        'product_type',
        'dg_icon_bitmap'
    ];

    id: string;
    shipment_id: string;
    shipment_package_id?: string;
    external_reference: string;
    number: number
    product_number: number;
    product_number_ean: string;
    product_text_1: string;
    product_text_2: string;
    product_text_3: string;
    image_url?: string;
    packaging_code?: string;
    amount: number;
    original_amount?: number;
    unit: string;
    status: string;
    shipment_status_formatted: string;
    note: string;
    note_modified: Moment
    created: Moment
    modified: Moment
    stock_updated: Moment
    stock_reserved: number;
    stock: number;
    stock_location?: string;
    purchase_order: number;
    amount_picked: number = 0;
    amount_scanned?: number = 0;
    weight?: number;
    volume?: number;
    width?: number;
    height?: number;
    length?: number;
    dg_un?: string;
    dg_class?: string;
    dg_packaging_group?: string;
    dg_description?: string;
    dg_packaging?: string;
    product_type?: string;
    dg_icon_bitmap?: number;
    dg_icons?: string[] = [];

    child_product_lines?: ShipmentProductLine[] = [];

    constructor(initializer?: ShipmentProductLineInterface) {
        if (initializer === undefined) {
            return;
        }
    }

    static fromJSON(json: ShipmentProductLineInterface | string): ShipmentProductLine {
        if (json === null) {
            return new ShipmentProductLine();
        }

        if (typeof json === 'string') {
            return JSON.parse(json, ShipmentProductLine.reviver);
        } else {
            const shipmentProductLine = new ShipmentProductLine();

            const extra = {
                note_modified: json.note_modified ? moment(json.note_modified) : null,
                stock_updated: json.stock_updated ? moment(json.stock_updated) : null,
                created: moment(json.created),
                modified: moment(json.modified),
                child_product_lines: [],
                dg_icons: ShipmentProductLine.dgIconUrls(json.dg_icon_bitmap),
            };

            Object.keys(json).forEach((key, index) => {
                if (ShipmentProductLine._JSONProps.indexOf(key) !== -1) {
                    shipmentProductLine[key] = json[key];
                }
            });

            return Object.assign(shipmentProductLine, extra);
        }
    }

    static reviver(key: string, value: any): any {
        return key === "" ? ShipmentProductLine.fromJSON(value) : value;
    }

    /**
     * Converts a dg_icon_bitmap integer (Bitmap13, where position 0 is the least
     * significant bit) into a list of icon urls, one per enabled position.
     */
    static dgIconUrls(bitmap?: number | string): string[] {
        const value = Number(bitmap);
        if (!bitmap || !Number.isInteger(value)) {
            return [];
        }

        const urls: string[] = [];
        for (let position = 0; position < DG_ICON_COUNT; position++) {
            if (Math.floor(value / Math.pow(2, position)) % 2 === 1) {
                urls.push(`${environment.assetBase}/assets/img/dg-icons/${position}.png`);
            }
        }

        return urls;
    }

    public formattedStockStatus?(): string {

        let status = this.orderLineStatus();

        if (status === OrderLineStatus.Ok) {
            return 'På lager';
        } else if (status === OrderLineStatus.Warning) {
            return 'Lavt lager';
        } else if (status === OrderLineStatus.Error) {
            return 'Ingen lager';
        } else {
            return 'Ukendt lager';
        }
    }

    public orderLineStatus?(): OrderLineStatus {

        let now = moment();
        if (!this.stock_updated) {
            return OrderLineStatus.Unknown;
        }

        if (this.stock_updated && this.stock_updated.isValid()) {
            let diff = now.diff(this.stock_updated, 'minutes');
            if (diff > 60) {
                return OrderLineStatus.Unknown;
            }
        }

        if (this.stock > 10) {
            return OrderLineStatus.Ok
        } else if (this.stock < 10 && this.stock > 0) {
            return OrderLineStatus.Warning
        } else {
            return OrderLineStatus.Error
        }
    }

    public stockStatusColor?(showLabel?: boolean): any {

        let status = this.orderLineStatus();
        let color;
        if (status === OrderLineStatus.Ok) {
            color = 'status-box--green';
        } else if (status === OrderLineStatus.Warning) {
            color = 'status-box--orange';
        } else if (status === OrderLineStatus.Error) {
            color = 'status-box--red';
        } else {
            color = 'status-box--gray';
        }

        let rt = {
            'status-box--large': showLabel,
        };
        rt[color] = true;

        return rt;
    }
}

// A representation of Token data that can be converted to
// and from JSON without being altered.
export interface ShipmentProductLineInterface extends ShipmentProductLine {
}

export enum OrderLineStatus {
    Ok,
    Warning,
    Error,
    Unknown
}
